import { useTheme } from 'styled-components';
import useClientFormController from '../../../1_application/client/clientFormController';
import { Client } from '../../../2_domain/client/client';
import Column from '../../core/components/box/column';
import Row from '../../core/components/box/row';
import SizedBox from '../../core/components/box/sizedBox';
import { LXLabelSelector } from '../../core/components/buttons/lxSelector';
import { LXLabelTwoRadio } from '../../core/components/buttons/lxTwoRadio';
import Visibility, {
  Displayable,
} from '../../core/components/visibility/visibility';
import LXInput, { LXLabelInput } from '../../core/texts/lxInput';
import { LXbody } from '../../core/texts/lxText';

interface ClientFormProps {
  client?: Client;
  onClientChange: (newClient: Client) => void;
  onClientValid: (isValid: boolean) => void;
}

const ClientForm: React.FC<ClientFormProps> = (props) => {
  const theme = useTheme();
  const c = useClientFormController(
    props.onClientChange,
    props.onClientValid,
    props.client
  );

  return (
    <Column>
      <Row>
        <LXLabelInput
          label={theme.texts.name}
          width={318}
          value={c.name}
          onChange={c.setName}
          tabIndex={1}
        ></LXLabelInput>
        <SizedBox width={24}></SizedBox>
        <LXLabelInput
          placeholder={'dd'}
          label={theme.texts.dateOfBirth}
          width={90}
          value={c.birth.day}
          onValidate={c.birth.setIsDayValid}
          validate={c.birth.dayValidate}
          onChange={c.birth.setDay}
          validateDelay={10}
          tabIndex={2}
        ></LXLabelInput>
        <SizedBox width={8}></SizedBox>
        <LXLabelInput
          placeholder={'mm'}
          label={''}
          width={90}
          value={c.birth.month}
          onValidate={c.birth.setIsMonthValid}
          validate={c.birth.monthValidate}
          onChange={c.birth.setMonth}
          validateDelay={10}
          tabIndex={3}
        ></LXLabelInput>
        <SizedBox width={8}></SizedBox>
        <LXLabelInput
          placeholder={'yyyy'}
          label={''}
          width={122}
          value={c.birth.year}
          onValidate={c.birth.setIsYearValid}
          validate={c.birth.yearValidate}
          onChange={c.birth.setYear}
          validateDelay={10}
          tabIndex={4}
        ></LXLabelInput>
      </Row>
      <SizedBox height={24}></SizedBox>
      <Row>
        <LXLabelSelector<Gender>
          label={theme.texts.gender}
          placeholder={theme.texts.selectGender}
          value={c.gender}
          values={['MALE', 'FEMALE']}
          labels={[theme.texts.male, theme.texts.female]}
          onSelect={c.setGender}
          dropdownHeaderWidth={318}
          dropdownItemWidth={318}
          tabIndex={5}
        ></LXLabelSelector>
        <SizedBox width={24}></SizedBox>
        <Visibility visible={c.gender === 'FEMALE'}>
          <LXLabelTwoRadio<Pregnancy>
            label={theme.texts.pregnancy}
            value={c.pregnancy}
            values={[true, false]}
            labels={[theme.texts.yes, theme.texts.notYes]}
            onSelect={c.setPregnancy}
            width={'318px'}
            tabIndex={8}
          ></LXLabelTwoRadio>
        </Visibility>
      </Row>
      <Displayable visible={c.gender === 'FEMALE' && c.pregnancy}>
        <SizedBox height={24} />
        <Column alignItems="flex-end">
          <Row>
            <LXbody color={theme.colors.title}>
              {theme.texts.lastMenstrualDate}
            </LXbody>
            <SizedBox width={204} />
          </Row>
          <SizedBox height={8}></SizedBox>
          <Row justifyContent="flex-end">
            <LXInput
              placeholder={'dd'}
              width={90}
              value={c.lastPeriod.day}
              onValidate={c.lastPeriod.setIsDayValid}
              validate={c.lastPeriod.dayValidate}
              onChange={c.lastPeriod.setDay}
              validateDelay={10}
              tabIndex={2}
            ></LXInput>
            <SizedBox width={8}></SizedBox>
            <LXInput
              placeholder={'mm'}
              width={90}
              value={c.lastPeriod.month}
              onValidate={c.lastPeriod.setIsMonthValid}
              validate={c.lastPeriod.monthValidate}
              onChange={c.lastPeriod.setMonth}
              validateDelay={10}
              tabIndex={3}
            ></LXInput>
            <SizedBox width={8}></SizedBox>
            <LXInput
              placeholder={'yyyy'}
              width={122}
              value={c.lastPeriod.year}
              onValidate={c.lastPeriod.setIsYearValid}
              validate={c.lastPeriod.yearValidate}
              onChange={c.lastPeriod.setYear}
              validateDelay={10}
              tabIndex={4}
            ></LXInput>
          </Row>
        </Column>
      </Displayable>
      <SizedBox height={24}></SizedBox>
      <Row>
        <LXLabelSelector<Nationality>
          label={theme.texts.nationality}
          placeholder={theme.texts.selectNationality}
          value={c.nationality}
          values={['INDONESIA', 'FOREIGN']}
          labels={[theme.texts.indonesia, theme.texts.foreigner]}
          onSelect={c.setNationality}
          dropdownHeaderWidth={318}
          dropdownItemWidth={318}
          tabIndex={10}
        ></LXLabelSelector>
        <SizedBox width={24}></SizedBox>
        {c.nationality === 'INDONESIA' && (
          <LXLabelInput
            label={theme.texts.nik}
            placeholder={theme.texts.enterIDNumber}
            value={c.idNumber}
            onChange={c.setIdNumber}
            width={318}
            tabIndex={13}
          ></LXLabelInput>
        )}
        {c.nationality === 'FOREIGN' && (
          <LXLabelInput
            label={theme.texts.passportNumber}
            placeholder={theme.texts.enterPassportNumber}
            width={318}
            value={c.passportNumber}
            onChange={c.setPassportNumber}
            tabIndex={13}
          ></LXLabelInput>
        )}
      </Row>
      <SizedBox height={24}></SizedBox>
      <Row>
        <LXLabelInput
          label={theme.texts.phone}
          placeholder={theme.texts.enterPhoneNumber}
          width={318}
          value={c.phone}
          onChange={c.setPhone}
          tabIndex={14}
        ></LXLabelInput>
        <SizedBox width={24}></SizedBox>
        <LXLabelInput
          label={theme.texts.email}
          placeholder={theme.texts.exampleEmail}
          width={318}
          value={c.email}
          onChange={c.setEmail}
          tabIndex={15}
        ></LXLabelInput>
      </Row>
      <SizedBox height={24}></SizedBox>
      <LXLabelInput
        label={theme.texts.memo + ` (${theme.texts.optional})`}
        width={660}
        height={200}
        placeholder={theme.texts.enterSomethingRelatedToTheClient}
        isMultiline={true}
        value={c.memo}
        onChange={c.setMemo}
        tabIndex={16}
      ></LXLabelInput>
    </Column>
  );
};

export default ClientForm;
